import { BarChart3, Briefcase, Building2, CircleUser, Mail, MapPin, Phone, TrendingUp } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CadrageReadWithTeam } from '../../../types'; // Ajuster le chemin si nécessaire
 // Importer des icônes pertinentes

interface Props {
  cadrage: CadrageReadWithTeam | null;
  isLoading: boolean;
  isReadOnly?: boolean; // Mode lecture seule
}

const DetailItem: React.FC<{ icon: React.ElementType; label: string; value: string | number | null | undefined }> = ({ icon: Icon, label, value }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-start space-x-3">
      <Icon className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" aria-hidden="true" />
      <div className="flex-1">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd className="mt-1 text-sm text-foreground">{value || <span className="italic text-muted-foreground">{t('global.notDefined')}</span>}</dd>
      </div>
    </div>
  );
};


const CadrageCompanyInfoSection: React.FC<Props> = ({ cadrage, isLoading, isReadOnly = false }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <div className="bg-card rounded-lg shadow p-6 animate-pulse"><div className="h-4 bg-muted rounded w-3/4"></div></div>;
  }

  if (!cadrage) {
    return null; // Ou afficher un message si nécessaire
  }

  // Extraire les infos société du cadrage
  const {
    company_name,
    company_sector,
    company_size,
    company_revenue,
    company_location,
    main_contact_name,
    main_contact_role,
    main_contact_email,
    main_contact_phone,
  } = cadrage;

  // Vérifier si au moins une information société est présente
  const hasCompanyInfo = [
    company_name, company_sector, company_size, company_revenue, company_location,
    main_contact_name, main_contact_role, main_contact_email, main_contact_phone
  ].some(info => info);

  return (
    <div className="bg-card rounded-lg shadow overflow-hidden">
      <div className="px-6 py-5">
        <h3 className="text-lg font-semibold leading-6 text-foreground flex items-center">
          <Building2 className="h-5 w-5 mr-2 text-muted-foreground" />
          {t('cadrage.company.title')}
        </h3>
      </div>
      <div className="border-t border-border px-6 py-5">
        {hasCompanyInfo ? (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
            <DetailItem icon={Building2} label={t('cadrage.company.fields.companyName')} value={company_name} />
            <DetailItem icon={Briefcase} label={t('cadrage.company.fields.sector')} value={company_sector} />
            <DetailItem icon={BarChart3} label={t('cadrage.company.fields.size')} value={company_size} />
            <DetailItem icon={TrendingUp} label={t('cadrage.company.fields.revenue')} value={company_revenue} />
            <DetailItem icon={MapPin} label={t('cadrage.company.fields.location')} value={company_location} />

            {/* Séparateur visuel pour le contact */}
            <div className="sm:col-span-2 lg:col-span-3 my-2"></div>

            <h4 className="text-md font-medium text-foreground sm:col-span-2 lg:col-span-3 mb-[-10px] flex items-center">
              <CircleUser className="h-5 w-5 mr-2 text-muted-foreground" />
              {t('cadrage.company.mainContact')}
            </h4>
            <DetailItem icon={CircleUser} label={t('cadrage.company.fields.contactName')} value={main_contact_name} />
            <DetailItem icon={Briefcase} label={t('cadrage.company.fields.contactRole')} value={main_contact_role} />
            <DetailItem icon={Mail} label={t('cadrage.company.fields.contactEmail')} value={main_contact_email} />
            <DetailItem icon={Phone} label={t('cadrage.company.fields.contactPhone')} value={main_contact_phone} />
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground italic">{t('cadrage.company.noInfo')}</p>
        )}
      </div>
    </div>
  );
};

export default CadrageCompanyInfoSection;
